"use client";

import { FormRenderer } from "@atlas/composite";
import {
  Card,
  CardContent,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Label,
} from "@atlas/core";
import { useFormBuilder, useTranslation } from "@atlas/sdk";
import { FileText } from "lucide-react";

import type { UseFormReturn } from "react-hook-form";

export interface ICatalogTemplateForm {
  formId: number;
  type: "catalogType" | "catalog";
  label?: string;
  namePrefix?: string;
}

interface ICatalogTemplateStepProps {
  forms?: ICatalogTemplateForm[];
  form?: UseFormReturn<any>;
  isFieldsDisabled?: boolean;
  title?: string | null;
  description?: string;
  emptyMessage?: string;
}

function SingleFormRenderer({
  form,
  parentForm,
  isFieldsDisabled = true,
}: {
  form: ICatalogTemplateForm;
  parentForm?: UseFormReturn<any>;
  isFieldsDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const { data: formData, isLoading: formLoading } = useFormBuilder(
    form.formId,
    {
      enabled: form.formId > 0,
    },
  );

  const resolvedConfig = formData?.formConfig as string;
  const isFetchingForm = form.formId > 0 ? formLoading : false;

  let hasItems = true;
  if (resolvedConfig) {
    try {
      const parsed = JSON.parse(resolvedConfig);
      if (
        !parsed?.items ||
        (Array.isArray(parsed.items) && parsed.items.length === 0)
      ) {
        hasItems = false;
      } else if (parsed?.fields && parsed?.fields.length === 0) {
        hasItems = false;
      }
    } catch {
      // Ignore parse error
    }
  } else if (!isFetchingForm) {
    hasItems = false;
  }

  if (!hasItems) {
    return (
      <Card isLoading={isFetchingForm} disabled={isFieldsDisabled}>
        <CardContent className="pt-6">
          <Empty className="mx-auto">
            <EmptyMedia>
              <FileText className="h-6 w-6 text-muted-foreground" />
            </EmptyMedia>
            <EmptyHeader>
              <EmptyTitle>
                {form.type === "catalogType"
                  ? t("hub.create_catalog.no_form_fields_available_type", {
                      defaultValue:
                        "No form fields available for this catalog type.",
                    })
                  : t("hub.create_catalog.no_form_fields_available_catalog", {
                      defaultValue:
                        "No form fields available for this catalog.",
                    })}
              </EmptyTitle>
            </EmptyHeader>
          </Empty>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card isLoading={isFetchingForm} disabled={isFieldsDisabled}>
      <CardContent className="pt-6 max-h-100 overflow-y-auto">
        <FormRenderer
          namePrefix={form.namePrefix}
          generatedJson={resolvedConfig}
          renderFormTag={false}
          showSubmitButton={false}
          showResetButton={false}
          isFieldsDisabled={isFieldsDisabled}
          form={parentForm}
        />
      </CardContent>
    </Card>
  );
}

export function CatalogTemplateStep({
  forms,
  form: parentForm,
  isFieldsDisabled = true,
  title,
  description,
  emptyMessage,
}: ICatalogTemplateStepProps) {
  const { t } = useTranslation();

  const resolvedForms: ICatalogTemplateForm[] = forms ?? [];

  const hasForms = resolvedForms.length > 0;
  if (!hasForms) {
    return (
      <div className="flex flex-col gap-4">
        {title ? (
          <div className="space-y-2">
            <CardTitle className="font-semibold">{title}</CardTitle>
            {description && (
              <Label className="text-sm text-muted-foreground leading-relaxed">
                {description}
              </Label>
            )}
          </div>
        ) : title === undefined ? (
          <CardTitle>
            {t("hub.create_catalog.configuration", {
              defaultValue: "Configuration",
            })}
          </CardTitle>
        ) : null}
        <Card variant="content" className="border-0 shadow-none">
          <CardContent>
            <Label className="text-muted-foreground">
              {emptyMessage ??
                t("hub.create_catalog.select_catalog_type_first", {
                  defaultValue:
                    "Please select a catalog type in the previous step to configure the form.",
                })}
            </Label>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {title ? (
        <div className="space-y-2">
          <CardTitle className="font-semibold">{title}</CardTitle>
          {description && (
            <Label className="text-sm text-muted-foreground leading-relaxed">
              {description}
            </Label>
          )}
        </div>
      ) : title === undefined ? (
        <CardTitle>
          {t("hub.create_catalog.configuration", {
            defaultValue: "Configuration",
          })}
        </CardTitle>
      ) : null}
      {resolvedForms.map((form) => (
        <SingleFormRenderer
          key={`id-${form.formId}`}
          form={form}
          parentForm={parentForm}
          isFieldsDisabled={isFieldsDisabled}
        />
      ))}
    </div>
  );
}
